import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUserFavourites, getUserFavouriteGames, addToFavorites, removeFromFavorites } from '../api/games';
import { useAuth } from '../AuthContext';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]); // Store game IDs for quick lookup
  const [favoriteGames, setFavoriteGames] = useState([]); // Store full game objects for ProfilePage
  const [loading, setLoading] = useState(false);

  const loadFavorites = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Load both favorite IDs and full game data
      const [userFavoriteIds, userFavoriteGames] = await Promise.all([
        getUserFavourites(),
        getUserFavouriteGames()
      ]);
      
      console.log('🎯 Loaded favorites:', { userFavoriteIds, userFavoriteGames });
      
      setFavorites(userFavoriteIds);
      setFavoriteGames(userFavoriteGames);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavorites([]);
      setFavoriteGames([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load user's favorites when user changes
  useEffect(() => {
    if (user) {
      loadFavorites();
    } else {
      setFavorites([]);
      setFavoriteGames([]);
    }
  }, [user, loadFavorites]);

  const isFavorite = (gameId) => {
    const idNum = Number(gameId);
    const isFav = favorites.some(favId => Number(favId) === idNum);
    return isFav;
  };

  const toggleFavorite = async (gameId) => {
    if (!user) {
      console.warn('User not authenticated');
      return false;
    }

    try {
      const gameIdInt = Number(gameId);
      const isCurrentlyFavorite = isFavorite(gameIdInt);

      if (isCurrentlyFavorite) {
        // Remove from favorites
        await removeFromFavorites(gameIdInt);
        setFavorites(prev => {
          const newFavorites = prev.filter(favId => Number(favId) !== gameIdInt);
          console.log('Removed from favorites, new list:', newFavorites);
          return newFavorites;
        });
        // Also remove from favoriteGames
        setFavoriteGames(prev => prev.filter(game => Number(game.id) !== gameIdInt));
      } else {
        // Add to favorites
        await addToFavorites(gameIdInt);
        setFavorites(prev => {
          const newFavorites = [...prev, gameIdInt];
          console.log('Added to favorites, new list:', newFavorites);
          return newFavorites;
        });
        // Reload favorites to get the full game data
        setTimeout(() => loadFavorites(), 100);
      }

      return !isCurrentlyFavorite; // Return new favorite status
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return null; // Indicate error
    }
  };

  const value = {
    favorites, // Array of game IDs for quick lookup
    favoriteGames, // Array of full game objects for detailed views
    loading,
    isFavorite,
    toggleFavorite,
    loadFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
